import React, { useState, useEffect, useCallback } from "react";
import { Plus, Calendar, Play, CheckCircle, Clock, Filter } from "lucide-react";
import { LoginUserData } from "../LoginForm/LoginForm";
import { realAPI } from "../../services/realAPI";

interface SprintManagementProps {
  currentUser: LoginUserData;
  onStatsUpdate: () => void;
}

interface Sprint {
  id: string | number;
  organizationId: string | number;
  teamId: string | number;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: "planning" | "active" | "completed";
  createdAt: Date;
  createdBy: string;
}

interface Team {
  id: string | number;
  name: string;
  description: string;
  memberIds: string[];
}

const SprintManagement: React.FC<SprintManagementProps> = ({
  currentUser,
  onStatsUpdate,
}) => {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>("all");
  const [formData, setFormData] = useState({
    teamId: "",
    name: "",
    description: "",
    startDate: "",
    endDate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sprintErrors, setSprintErrors] = useState<Record<string, string>>({}); // Track errors per sprint

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [sprintsData, teamsData] = await Promise.all([
        realAPI.getSprintsByOrganization(currentUser.organizationId),
        realAPI.getTeamsByOrganization(currentUser.organizationId),
      ]);
      setSprints(sprintsData);
      setTeams(teamsData);
    } catch (error) {
      console.error("Error loading sprints:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser.organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getSprintsByTeam = () => {
    const filteredSprints =
      selectedTeamFilter === "all"
        ? sprints
        : sprints.filter(
            (sprint) => String(sprint.teamId) === String(selectedTeamFilter)
          );

    const grouped: Record<string, Sprint[]> = {};

    // Initialize with all teams (even if they have no sprints)
    teams.forEach((team) => {
      grouped[String(team.id)] = [];
    });

    // Add "Unassigned" group for sprints without a team
    grouped["unassigned"] = [];

    // Group the sprints
    filteredSprints.forEach((sprint) => {
      const teamIdString = String(sprint.teamId);
      if (sprint.teamId && grouped[teamIdString]) {
        grouped[teamIdString].push(sprint);
      } else {
        grouped["unassigned"].push(sprint);
      }
    });

    // Sort sprints by start date within each group
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
    });

    return grouped;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.teamId) newErrors.teamId = "Please select a team";
    if (!formData.name.trim()) newErrors.name = "Sprint name is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.endDate) newErrors.endDate = "End date is required";

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const now = new Date();

      if (start < now) {
        newErrors.startDate = "Start date cannot be in the past";
      }
      if (start >= end) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSprint = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await realAPI.createSprint({
        organizationId: currentUser.organizationId,
        teamId: formData.teamId,
        name: formData.name,
        description: formData.description,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        createdBy: currentUser.id,
      });

      setFormData({
        teamId: "",
        name: "",
        description: "",
        startDate: "",
        endDate: "",
      });
      setShowCreateForm(false);
      await loadData();
      onStatsUpdate();
    } catch (error) {
      console.error("Error creating sprint:", error);
      if (error instanceof Error) {
        setErrors({ general: error.message });
      }
    }
  };

  const handleStatusChange = async (
    sprintId: string,
    newStatus: "planning" | "active" | "completed"
  ) => {
    try {
      if (newStatus === "active") {
        const sprintToActivate = sprints.find(
          (sprint) => String(sprint.id) === String(sprintId)
        );

        if (!sprintToActivate) {
          throw new Error("Sprint not found");
        }

        const activeSprint = sprints.find(
          (sprint) =>
            sprint.teamId === sprintToActivate.teamId &&
            sprint.status === "active"
        );

        if (activeSprint) {
          throw new Error(
            `Only one sprint can be active per team. Currently active sprint: ${activeSprint.name}`
          );
        }
      }

      await realAPI.updateSprintStatus(sprintId, newStatus);
      await loadData();
      onStatsUpdate();
      setSprintErrors((prev) => ({ ...prev, [sprintId]: "" })); // Clear error for this sprint
    } catch (error) {
      console.error("Error updating sprint status:", error);
      const errorMessage =
        error instanceof Error
          ? error.message || "An error occurred."
          : "An unknown error occurred.";
      setSprintErrors((prev) => ({
        ...prev,
        [sprintId]: errorMessage,
      }));
    }
  };

  const getTeamName = (teamId: string) => {
    if (teamId === "unassigned") return "Unassigned";
    const team = teams.find((t) => String(t.id) === String(teamId));
    return team ? team.name : "Unknown Team";
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "planning":
        return <Clock className="w-4 h-4" />;
      case "active":
        return <Play className="w-4 h-4" />;
      case "completed":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "planning":
        return "bg-gray-100 text-gray-800";
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedSprints = getSprintsByTeam();

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Sprint Management</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <select
              value={selectedTeamFilter}
              onChange={(e) => setSelectedTeamFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Teams</option>
              {teams.map((team) => (
                <option key={team.id} value={String(team.id)}>
                  {team.name}
                </option>
              ))}
            </select>
            <Filter className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Sprint
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Create New Sprint
            </h3>

            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.general}</p>
              </div>
            )}

            <form onSubmit={handleCreateSprint}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team *
                  </label>
                  <select
                    value={formData.teamId}
                    onChange={(e) =>
                      setFormData({ ...formData, teamId: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.teamId ? "border-red-300" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select a team</option>
                    {teams.map((team) => (
                      <option key={team.id} value={String(team.id)}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  {errors.teamId && (
                    <p className="text-red-500 text-sm mt-1">{errors.teamId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sprint Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="Sprint 1"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Sprint goals and objectives..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.startDate ? "border-red-300" : "border-gray-300"
                      }`}
                      min={new Date().toISOString().split("T")[0]}
                    />
                    {errors.startDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.startDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.endDate ? "border-red-300" : "border-gray-300"
                      }`}
                      min={
                        formData.startDate ||
                        new Date().toISOString().split("T")[0]
                      }
                    />
                    {errors.endDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setErrors({});
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Create Sprint
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {Object.entries(groupedSprints).map(([teamId, teamSprints]) => {
          if (teamSprints.length === 0 && selectedTeamFilter !== "all")
            return null;

          return (
            <div
              key={teamId}
              className="bg-white rounded-lg shadow overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  {getTeamName(teamId)}
                  <span className="ml-2 bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
                    {teamSprints.length} sprint
                    {teamSprints.length !== 1 ? "s" : ""}
                  </span>
                </h3>
              </div>

              {teamSprints.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {teamSprints.map((sprint) => (
                    <div key={sprint.id} className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <h3 className="text-lg font-medium text-gray-900 mr-3">
                              {sprint.name}
                            </h3>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                sprint.status
                              )}`}
                            >
                              {getStatusIcon(sprint.status)}
                              <span className="ml-1 capitalize">
                                {sprint.status}
                              </span>
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2">
                            {sprint.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>
                              {formatDate(sprint.startDate)} -{" "}
                              {formatDate(sprint.endDate)}
                            </span>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          {sprint.status === "planning" && (
                            <button
                              onClick={() =>
                                handleStatusChange(String(sprint.id), "active")
                              }
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors flex items-center"
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Start
                            </button>
                          )}
                          {sprint.status === "active" && (
                            <button
                              onClick={() =>
                                handleStatusChange(
                                  String(sprint.id),
                                  "completed"
                                )
                              }
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors flex items-center"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Complete
                            </button>
                          )}
                        </div>
                      </div>

                      {sprintErrors[sprint.id] && (
                        <div className="text-red-500 text-sm mt-2">
                          {sprintErrors[sprint.id]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No sprints for this team
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(groupedSprints).length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No sprints found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedTeamFilter === "all"
                ? "Get started by creating a new sprint."
                : "No sprints match the selected team filter."}
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Sprint
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SprintManagement;
